import { TFunction } from 'i18next';
import uniqueId from 'lodash/uniqueId';
import {
  CreatableWorkflow,
  Workflow,
  WorkflowActions,
  WorkflowActionType,
  WorkflowAlert,
  WorkflowConditionals,
  WorkflowDetailSource,
  WorkflowPublishAlertAction,
  WorkflowPublishEventAction,
  WorkflowState,
  WorkflowStep,
  WorkflowStepBase,
  WorkflowStepType,
  WorkflowTaskHumanAction,
  WorkflowWaitAction,
} from '../data/models/workflow.model';
import {
  StartOnEventType,
  StartOnEventValue,
  StartOnStepValue,
  StepResult,
  WaitActionTimePeriod,
  WaitAmountUnit,
  WorkflowPublishEventActionParams,
  WorkflowStepFormActions,
  WorkflowStepFormData,
  WorkflowStepFormStartOn,
  WorkflowWaitActionParams,
} from '../data/workflow-form.model';
import { SystemEvent } from '../data/models/system-event.model';
import { SelectOptionGroup } from '../components/forms/GroupedSelectMenu';

export interface CreateWorkflowData {
  workQueueUUID: string;
  name: string;
  description?: string;
  state?: WorkflowState;
  workflowUUID?: string;
  steps: WorkflowStepFormData[];
}

type TriggerSteps = Pick<
  WorkflowStepBase,
  'onSuccess' | 'onFailure' | 'onCancel'
>;

type WorkingWorkflowStep = WorkflowStep & { tempId: string };

interface GroupedEventValues {
  groupName: string;
  key: string;
  value: string;
  selectedLabel: string;
  isDisabled: boolean;
}

export class WorkflowError extends Error {
  private messageKey: string;
  private messageArgs: any;

  constructor(message: string, messageKey: string, messageArgs?: any) {
    super(message);
    this.messageKey = messageKey;
    this.messageArgs = messageArgs;
  }

  public getTranslatedErrorMessage(translationFunc: TFunction): string {
    return translationFunc(this.messageKey, this.messageArgs ?? {}) as string;
  }
}

export const customInput = 'Custom Input';

class WorkflowUtils {
  /**
   * Transforms workflow form data into a standardized CreatableWorkflow object that conforms to API requirements.
   * @param data workflow form data
   * @returns a CreatableWorkflow object
   */
  public createWorkflowFromForm(data: CreateWorkflowData): CreatableWorkflow {
    const { workQueueUUID, name, description, steps } = data;
    if (!name) {
      throw new WorkflowError(
        'Workflow name is missing',
        'workflows.nameMissingError',
      );
    }
    let modelSteps: WorkflowStep[] = [];
    steps.forEach((step) => {
      const otherSteps = steps.filter((s) => s !== step);
      const modelStep = this.getWorkflowStepFromForm(step, otherSteps);
      modelSteps.push(modelStep);
    });

    const creatableWorkflow: CreatableWorkflow = {
      workQueueUUID,
      name,
      description,
      steps: modelSteps,
    };
    return creatableWorkflow;
  }

  /**
   * Transforms workflow step form data into a properly formatted WorkflowStep object compatible with the API specification.
   * @param step the current step
   * @param otherSteps all the other steps
   * @returns a WorkflowStep object
   */
  private getWorkflowStepFromForm(
    step: WorkflowStepFormData,
    otherSteps: WorkflowStepFormData[],
  ): WorkflowStep {
    const action = this.getWorkflowActionFromForm(step);
    if (!action) {
      throw new WorkflowError(
        `Action undefined for step named ${step.name}`,
        'workflows.actionMissingError',
        { name: step.name },
      );
    }
    const type: WorkflowStepType = step.isStart
      ? WorkflowStepType.Start
      : WorkflowStepType.Intermediate;
    const triggerEventNames = this.getTriggerEventNames(step.startOn);
    const triggerEvents = this.getSystemEventsWithVersion(triggerEventNames);
    const trigger =
      triggerEvents.length > 0 ? { events: triggerEvents } : undefined;
    if (!step.tempId) {
      throw new WorkflowError(
        `Step ${step.name} is missing id`,
        'workflows.stepIdMissingError',
        { name: step.name },
      );
    }
    const triggerSteps = this.getTriggerSteps(step.tempId, otherSteps);

    const baseWorkflow: WorkflowStepBase = {
      name: step.name,
      description: step.description,
      action,
      ...triggerSteps,
    };

    if (type === WorkflowStepType.Start) {
      if (!trigger) {
        throw new WorkflowError(
          'Start step missing trigger',
          'workflows.startStepMissingTriggerError',
        );
      }
      return {
        ...baseWorkflow,
        type: WorkflowStepType.Start,
        trigger,
      };
    } else {
      return {
        ...baseWorkflow,
        type: WorkflowStepType.Intermediate,
        trigger,
      };
    }
  }

  /**
   * Generates all the trigger steps for the current step
   * @param currentStepId the id of the current step
   * @param otherSteps all the other steps
   * @returns a TriggerSteps object containing all the trigger steps for the current step
   */
  private getTriggerSteps(
    currentStepId: string,
    otherSteps: WorkflowStepFormData[],
  ): TriggerSteps | undefined {
    const triggerSteps: TriggerSteps = {};
    otherSteps.forEach((step) => {
      const stepActions = step.startOn.filter(
        (s) => s.startOnType === StartOnEventType.Step,
      );
      stepActions.forEach((action) => {
        try {
          const stepValue = JSON.parse(action.startOnValue) as StartOnStepValue;
          if (stepValue.stepId === currentStepId) {
            switch (stepValue.status) {
              case StepResult.Success:
                triggerSteps.onSuccess = { triggerStep: step.name };
                break;
              case StepResult.Failure:
                triggerSteps.onFailure = { triggerStep: step.name };
                break;
              case StepResult.Canceled:
                triggerSteps.onCancel = { triggerStep: step.name };
                break;
            }
          }
        } catch (error) {
          throw new WorkflowError(
            'Error parsing JSON for start on value',
            'workflows.startOnJSONError',
          );
        }
      });
    });
    return Object.keys(triggerSteps).length === 0 ? undefined : triggerSteps;
  }

  /**
   * Generates a list of all the trigger event names for the current step
   * @param startOn the list of workflow start on elements from the form
   * @returns a list of all trigger event names
   */
  private getTriggerEventNames(startOn?: WorkflowStepFormStartOn[]): string[] {
    if (!startOn) {
      return [];
    }
    return startOn
      .filter((start) => start.startOnType === StartOnEventType.Event)
      .map((start) =>
        start.startOnValue === customInput
          ? start.startOnCustomValue
          : start.startOnValue,
      )
      .filter((eventName) => !!eventName) as string[];
  }

  /**
   * Generates the workflow action for the current step
   * @param step the current step
   * @returns the workflow action for the current step
   */
  private getWorkflowActionFromForm(
    step: WorkflowStepFormData,
  ): WorkflowActions {
    if (!step.action) {
      throw new WorkflowError(
        `Action undefined for step named ${step.name}`,
        'workflows.actionMissingError',
        { name: step.name },
      );
    }
    switch (step.action) {
      case WorkflowActionType.Succeed:
        return { succeed: {} };
      case WorkflowActionType.Cancel:
        return { cancel: {} };
      case WorkflowActionType.Fail:
        return { fail: {} };
      case WorkflowActionType.RejectTripLeg:
        // TODO: Add support for this action type
        throw new Error('RejectTripLeg currently not supported');
      case WorkflowActionType.PublishAlert:
        return this.getAlertAction(
          WorkflowActionType.PublishAlert,
          step.publishAlert,
        );
      case WorkflowActionType.TaskHuman:
        return this.getAlertAction(
          WorkflowActionType.TaskHuman,
          step.taskHuman,
        );
      case WorkflowActionType.Wait:
        return this.getWaitAction(step.wait);
      case WorkflowActionType.PublishEvent:
        return this.getPublishEventAction(step.publishEvent);
    }
  }

  /**
   * Generates the publish event action from the form data
   * @param pubEventAction the publish event action form data
   * @returns the publish event action
   */
  private getPublishEventAction(
    pubEventAction?: WorkflowPublishEventActionParams,
  ): WorkflowPublishEventAction {
    if (!pubEventAction) {
      throw new WorkflowError(
        `Publish event action is missing`,
        'workflows.pubEventMissingActionError',
      );
    }
    if (pubEventAction.detailSource === WorkflowDetailSource.Template) {
      if (!pubEventAction.detailTemplate) {
        throw new WorkflowError(
          `Publish event action is missing template`,
          'workflows.pubEventMissingTemplateError',
        );
      }
      return {
        publishEvent: {
          name: pubEventAction.name,
          detailSource: WorkflowDetailSource.Template,
          detailTemplate: pubEventAction.detailTemplate,
        },
      };
    } else {
      return {
        publishEvent: {
          name: pubEventAction.name,
          detailSource: pubEventAction.detailSource,
        },
      };
    }
  }

  /**
   * Generates the wait action from the form data
   * @param waitAction the wait action form data
   * @returns the wait action
   */
  private getWaitAction(
    waitAction?: WorkflowWaitActionParams,
  ): WorkflowWaitAction {
    if (
      !waitAction ||
      !waitAction.waitAmount ||
      !waitAction.waitAmountUnit ||
      !waitAction.waitBaseDateTimeRef ||
      !waitAction.waitTimePeriod
    ) {
      throw new WorkflowError(
        `Wait action is missing required params`,
        'workflows.waitActionMissingParamsError',
      );
    }
    const waitAmountInt = parseInt(waitAction.waitAmount ?? '0');
    let minutes = this.waitAmountToMinutes(
      waitAmountInt,
      waitAction.waitAmountUnit,
    );
    if (waitAction.waitTimePeriod === WaitActionTimePeriod.Before) {
      minutes *= -1;
    }
    return {
      wait: {
        baseDateTimeRef: waitAction.waitBaseDateTimeRef,
        deltaMinutes: minutes,
      },
    };
  }

  /**
   * Generates the alert action from the form data
   * @param type either the publish alert or the task human action
   * @param alert the alert form data
   * @returns a publish alert or task human action, depending on the value for 'type'
   */
  private getAlertAction(
    type: WorkflowActionType.PublishAlert | WorkflowActionType.TaskHuman,
    alert?: WorkflowAlert,
  ): WorkflowPublishAlertAction | WorkflowTaskHumanAction {
    if (!alert) {
      throw new WorkflowError(
        `Alert action is missing'`,
        'workflows.alertActionMissingError',
      );
    }
    const workflowAlert: WorkflowAlert = {
      alertEventBC: alert.alertEventBC,
      alertDescription: alert.alertDescription,
      alertTypeBC: alert.alertTypeBC,
      interactiveResponse: false,
    };
    if (alert.alertRecipientBC) {
      workflowAlert.alertRecipientBC = alert.alertRecipientBC;
    }
    if (type === WorkflowActionType.PublishAlert) {
      return {
        publishAlert: workflowAlert,
      };
    } else {
      return {
        taskHuman: workflowAlert,
      };
    }
  }
  /**
   * Converts a Workflow model instance into a CreateWorkflowData object
   * compatible with the UI form component. This transformation handles the
   * data structure differences between the backend workflow model and the
   * frontend form requirements.
   * @param data The source workflow model to transform
   * @returns A structured data object ready for form rendering
   */
  public createWorkflowFormFromModel(workflow: Workflow): CreateWorkflowData {
    const { workQueueUUID, workflowUUID, name, description, steps, state } =
      workflow;

    const workingSteps = steps.map((step) => ({
      ...step,
      tempId: uniqueId(),
    }));
    let formSteps: WorkflowStepFormData[] = [];
    workingSteps.forEach((step) => {
      const otherSteps = workingSteps.filter((s) => s !== step);
      const modelStep = this.getWorkflowStepFromModel(step, otherSteps);
      formSteps.push(modelStep);
    });

    const workflowFormData: CreateWorkflowData = {
      workQueueUUID,
      workflowUUID,
      name,
      description,
      state,
      steps: formSteps,
    };
    return workflowFormData;
  }

  /**
   * Transforms a backend workflow step model into a UI-compatible format.
   * This function converts a single WorkingWorkflowStep from the backend data model
   * into a WorkflowStepFormData object that the UI components can render properly.
   *
   * @param step The individual workflow step to transform
   * @param otherSteps all the other steps
   * @returns A formatted step object ready for UI consumption
   */
  public getWorkflowStepFromModel(
    step: WorkingWorkflowStep,
    otherSteps: WorkingWorkflowStep[],
  ): WorkflowStepFormData {
    const action = this.getWorkflowActionFromModel(step);
    if (!action) {
      throw Error(`Action undefined for step named ${step.name}`);
    }
    const isStart = step.type === WorkflowStepType.Start;
    const startOn: WorkflowStepFormStartOn[] = [
      ...this.getEventStartOn(step),
      ...this.getStepStartOn(step, otherSteps),
    ];
    return {
      name: step.name,
      description: step.description,
      startOn,
      tempId: step.tempId,
      isStart,
      ...action,
    };
  }

  /**
   * Parses the event-based trigger configurations from a backend
   * WorkflowStep model and transforms them into WorkflowStepFormStartOn objects
   * that can be properly rendered and manipulated in the UI.
   * @param step The backend workflow step containing event trigger definitions
   * @returns An array of formatted event trigger conditions ready for UI display
   */
  private getEventStartOn(step: WorkflowStep): WorkflowStepFormStartOn[] {
    const startOn: WorkflowStepFormStartOn[] = [];
    if (step.trigger) {
      step.trigger.events.forEach((event) => {
        startOn.push({
          startOnType: StartOnEventType.Event,
          startOnValue: this.getVersionedSystemEventName(
            event.name,
            event.version,
          ), // UI will need to determine if this is a custom value
        });
      });
    }
    return startOn;
  }

  /**
   * Identifies and transforms step-based dependencies from a
   * WorkingWorkflowStep into structured WorkflowStepFormStartOn objects for the UI.
   * @param step The workflow step to extract trigger conditions from
   * @param otherSteps All other steps
   * @returns An array of formatted step trigger conditions ready for UI consumption
   */
  private getStepStartOn(
    step: WorkingWorkflowStep,
    otherSteps: WorkingWorkflowStep[],
  ): WorkflowStepFormStartOn[] {
    const currentStepName = step.name;
    const startOn: WorkflowStepFormStartOn[] = [];
    const addStartOnStep = (stepId: string, status: StepResult) => {
      const startOnStepValue: StartOnStepValue = {
        stepId,
        status,
      };
      startOn.push({
        startOnType: StartOnEventType.Step,
        startOnValue: JSON.stringify(startOnStepValue),
      });
    };
    otherSteps.forEach((step) => {
      if (step.onSuccess?.triggerStep === currentStepName) {
        addStartOnStep(step.tempId, StepResult.Success);
      }
      if (step.onFailure?.triggerStep === currentStepName) {
        addStartOnStep(step.tempId, StepResult.Failure);
      }
      if (step.onCancel?.triggerStep === currentStepName) {
        addStartOnStep(step.tempId, StepResult.Canceled);
      }
    });
    return startOn;
  }

  /**
   * Extracts action definitions from a WorkflowStep model and
   * converts them into a structured WorkflowStepFormActions object that the UI
   * can properly render and manipulate.
   * @param step The workflow step containing action definitions to transform
   *
   * @returns A formatted action configuration ready for UI display and interaction
   */
  private getWorkflowActionFromModel(
    step: WorkflowStep,
  ): WorkflowStepFormActions {
    const actionName = Object.keys(step.action)[0] as WorkflowActionType;
    const formActions: WorkflowStepFormActions = {
      action: actionName,
    };
    switch (actionName) {
      case WorkflowActionType.RejectTripLeg:
        // TODO: Update when reject trip leg is supported
        break;
      case WorkflowActionType.Wait:
        formActions.wait = this.getWaitActionParams(
          step.action as WorkflowWaitAction,
        );
        break;
      case WorkflowActionType.PublishAlert:
        formActions.publishAlert = (
          step.action as WorkflowPublishAlertAction
        ).publishAlert;
        break;
      case WorkflowActionType.TaskHuman:
        formActions.taskHuman = (
          step.action as WorkflowTaskHumanAction
        ).taskHuman;
        break;
      case WorkflowActionType.PublishEvent:
        formActions.publishEvent = (step.action as WorkflowPublishEventAction)
          .publishEvent as WorkflowPublishEventActionParams;
        break;
    }
    return formActions;
  }

  private getSystemEventsWithVersion(events: string[]) {
    return events.map((event) => {
      const tokens = event.split(' ');
      if (tokens.length === 1) {
        // Custom events wont have a version
        return {
          name: event,
          version: 1,
        };
      }
      const lastToken = tokens[tokens.length - 1];
      const version = parseInt(lastToken.substring(1));
      return {
        name: tokens.slice(0, -1).join(' '),
        version,
      };
    });
  }

  /**
   * Processes WorkflowWaitAction configurations from the backend
   * and converts them into structured WorkflowWaitActionParams that the UI components
   * can properly render and manipulate.
   * @param actions The backend wait action configuration to transform
   * @returns A formatted wait action parameter object ready for UI consumption
   */
  public getWaitActionParams(
    actions: WorkflowWaitAction,
  ): WorkflowWaitActionParams {
    const wait = actions.wait;
    const { deltaMinutes, baseDateTimeRef } = wait;
    const waitTimePeriod: WaitActionTimePeriod =
      deltaMinutes < 0
        ? WaitActionTimePeriod.Before
        : WaitActionTimePeriod.After;
    const absoluteDeltaMinutes = Math.abs(deltaMinutes);
    let waitAmount: string;
    let waitAmountUnit: WaitAmountUnit;
    if (absoluteDeltaMinutes % (60 * 24) === 0) {
      waitAmountUnit = WaitAmountUnit.Days;
      waitAmount = `${absoluteDeltaMinutes / (60 * 24)}`;
    } else if (absoluteDeltaMinutes % 60 === 0) {
      waitAmountUnit = WaitAmountUnit.Hours;
      waitAmount = `${absoluteDeltaMinutes / 60}`;
    } else {
      waitAmountUnit = WaitAmountUnit.Minutes;
      waitAmount = `${absoluteDeltaMinutes}`;
    }
    return {
      waitAmount,
      waitAmountUnit,
      waitTimePeriod,
      waitBaseDateTimeRef: baseDateTimeRef,
    };
  }

  /**
   * Updates step start on events, given the system events. Checks
   * if any event values are not system events. If the event value is not
   * a system event, copies the value to the custom value attribute
   * @param steps List of all steps
   * @param systemEvents List of all system events
   */
  public updateStepStartOnWithSystemEvents(
    steps: WorkflowStepFormData[],
    systemEvents: SystemEvent[],
  ) {
    steps.forEach((step) => {
      step.startOn.forEach((startOn) => {
        const systemEventNames = systemEvents.map((systemEvent) =>
          this.getVersionedSystemEventName(
            systemEvent.name,
            systemEvent.version,
          ),
        );
        const { startOnType, startOnValue } = startOn;
        if (
          startOnType === StartOnEventType.Event &&
          startOnValue &&
          startOnValue !== customInput &&
          !systemEventNames.includes(startOnValue)
        ) {
          // remove version number from custom value
          startOn.startOnCustomValue = `${startOnValue.split(' ')[0]}`;
          startOn.startOnValue = customInput;
        }
      });
    });
  }

  public getWorkflowStepFormDefaults(): WorkflowStepFormData {
    return {
      name: '',
      description: '',
      startOn: [],
      tempId: uniqueId(),
    };
  }

  public getAllowedStates(state: WorkflowState): WorkflowState[] {
    switch (state) {
      case WorkflowState.Draft:
        return [WorkflowState.Draft, WorkflowState.Active];
      case WorkflowState.Active:
        return [WorkflowState.Active, WorkflowState.Inactive];
      case WorkflowState.Inactive:
        return [
          WorkflowState.Inactive,
          WorkflowState.Active,
          WorkflowState.Deleted,
        ];
      default:
        return [];
    }
  }

  /**
   * Returns all possible values that can be selected for the step event, grouped by step name
   * @param otherSteps All other workflow steps (not including the current one)
   * @param startOnFields All start on fields
   * @param currentIndex The index of the current step in the start on fields array
   * @returns An array containing all the possible step event values, grouped by step name
   */
  public getStepEventValues(
    otherSteps: WorkflowStepFormData[],
    startOnFields: WorkflowStepFormStartOn[],
    currentIndex: number,
  ): SelectOptionGroup[] {
    const stepEventValues = otherSteps.map((step) => {
      let actions: GroupedEventValues[] = [];
      Object.values(StepResult).forEach((status) => {
        const value = { stepId: step.tempId ?? '', status };
        const otherStepStartOnActions = otherSteps
          .map((step) => step.startOn)
          .flat()
          .filter((step) => step.startOnType === StartOnEventType.Step);
        const currentStepStartOnActions = startOnFields.filter(
          (field, fieldIndex) =>
            currentIndex !== fieldIndex &&
            field.startOnType === StartOnEventType.Step &&
            field.startOnValue,
        );
        const allStepStartOnActions = [
          ...otherStepStartOnActions,
          ...currentStepStartOnActions,
        ];
        // Check if the action has already been used elsewhere. If yes,
        // then disable this action so that it cant be selected twice
        const usedAction = allStepStartOnActions.find((action) => {
          try {
            const actionValue = JSON.parse(
              action.startOnValue,
            ) as StartOnStepValue;
            return (
              actionValue.stepId === value.stepId &&
              actionValue.status === value.status
            );
          } catch (error) {
            return false;
          }
        });
        return actions.push({
          groupName: step.name,
          key: status,
          value: JSON.stringify(value),
          selectedLabel: `${step.name} > ${status}`,
          isDisabled: !!usedAction,
        });
      });
      return actions;
    });
    const flattenedValues = stepEventValues.flat();
    // Group the options by step
    const groupMap = flattenedValues.reduce((map, item) => {
      const key = item.groupName;

      if (!map.has(key)) {
        map.set(key, []);
      }

      map.get(key)!.push(item);
      return map;
    }, new Map<string, GroupedEventValues[]>());

    return Array.from(groupMap.entries()).map(([groupName, options]) => ({
      groupName,
      options,
    }));
  }

  /**
   * Checks if a step has any references to other steps or any references from other steps
   * @param currentStep the step to check for references
   * @param otherSteps all other steps in the workflow
   * @returns true if the step has references to/from another step
   */
  public doesStepHaveReferences(
    currentStep: WorkflowStepFormData,
    otherSteps: WorkflowStepFormData[],
  ) {
    // Check if current step has any step actions
    const hasStepActions = currentStep.startOn.some(
      (action) => action.startOnType === StartOnEventType.Step,
    );
    if (hasStepActions) {
      return true;
    }
    // Check if any other step has a step action that references the current step
    return otherSteps.some((step) => {
      return step.startOn
        .filter((startOn) => startOn.startOnType === StartOnEventType.Step)
        .some((startOn) => {
          try {
            const action = JSON.parse(startOn.startOnValue) as StartOnStepValue;
            return action.stepId === currentStep.tempId;
          } catch (error) {
            return false;
          }
        });
    });
  }

  /**
   * Deletes all references to a step with the given id
   * @param stepId the step id
   * @param otherSteps all other steps in the workflow
   */
  public deleteStepReferences(
    stepId: string,
    otherSteps: WorkflowStepFormData[],
  ) {
    otherSteps.forEach((step) => {
      step.startOn = step.startOn.filter((startOn) => {
        if (startOn.startOnType !== StartOnEventType.Step) {
          return true;
        }
        try {
          const action = JSON.parse(startOn.startOnValue) as StartOnStepValue;
          return action.stepId !== stepId;
        } catch (error) {
          return true;
        }
      });
    });
  }

  public waitAmountToMinutes(
    waitAmount: number,
    waitAmountUnit: WaitAmountUnit,
  ) {
    let minutes = waitAmount;
    if (waitAmountUnit === WaitAmountUnit.Days) {
      minutes *= 60 * 24;
    } else if (waitAmountUnit === WaitAmountUnit.Hours) {
      minutes *= 60;
    }
    return minutes;
  }

  public getVersionedSystemEventName(name: string, version: number) {
    return `${name} v${version}`;
  }

  public getConditionalNames() {
    return WorkflowConditionals.map((conditional) => conditional.name);
  }

  public getConditionalBC = (name: string): string | undefined => {
    return WorkflowConditionals.find((conditional) => conditional.name === name)
      ?.bc;
  };

  public getConditionalName = (bc: string): string | undefined => {
    return WorkflowConditionals.find((conditional) => conditional.bc === bc)
      ?.name;
  };
}

const workflowUtils = new WorkflowUtils();

export default workflowUtils;
